import React from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Dropdown, Flex, Input, Select, type MenuProps } from 'antd'
import { DropdownArrowIcon } from '../ant-overrides/icon-overrides'
import type { InsertableWorkflowParameter } from '../../../shared/domain/workflowParameter/types.no-deps'
import { ConfirmModal } from '../modals/confirmModal'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import { IconButton } from '../ui/IconButton'
import { PreventClickPropagation } from '../util/PreventClickPropagation'
import { InfoPopover } from '../ui/InfoPopover'

type Props = {
    parameters: InsertableWorkflowParameter[]
    onChange: (parameters: InsertableWorkflowParameter[]) => void
    parameterTypes: Array<{ id: number; name: string }>
}

export function WorkflowParameterSettings(props: Props) {
    const [isFormOpen, setIsFormOpen] = React.useState(false)
    const [deletingParameter, setDeletingParameter] = React.useState<UUID | undefined>(undefined)
    const [editParameter, setEditParameter] = React.useState<InsertableWorkflowParameter>(undefined)

    function deleteParameter() {
        props.onChange(props.parameters.filter((param) => param.id !== deletingParameter))
        setDeletingParameter(undefined)
    }

    function addNewParameter(name: string, unitType: number, editingParameterId?: UUID) {
        if (editingParameterId) {
            const editingParameterIndex = props.parameters.findIndex((param) => param.id === editingParameterId)
            const updatedParameters = [...props.parameters]
            // make sure we don't update the unitType here as it can do a lot of damage by unlinking from schedules etc.
            updatedParameters[editingParameterIndex] = {
                ...updatedParameters[editingParameterIndex],
                name,
            }
            props.onChange(updatedParameters)
        } else {
            props.onChange([
                ...props.parameters,
                {
                    id: uuidv4() as UUID,
                    name,
                    referenceId: unitType,
                },
            ])
        }
    }

    return (
        <>
            <div className={'AppCore__WorkflowParameterSettings__container'}>
                <div className="AppCore__WorkflowParameterSettings__header_container">
                    <span className="AppCore__WorkflowParameterSettings__title">
                        <Flex gap="4px" align="center">
                            {window.i18n('appcore:workflowParameters.titles.main')}
                            <InfoPopover title={window.i18n('appcore:workflowParameters.tooltips.title')} />
                        </Flex>
                    </span>
                    <zaptic-button
                        data-insight="workflow-parameter-settings-add-button"
                        onClick={() => {
                            setEditParameter(undefined)
                            setIsFormOpen(true)
                        }}
                        size="small"
                    >
                        {window.i18n('appcore:workflowParameters.buttons.add')}
                    </zaptic-button>
                </div>

                <div className="AppCore__WorkflowParameterSettings__parameter_headers">
                    <span>{window.i18n('appcore:workflowParameters.headers.name')}</span>
                    <span>{window.i18n('appcore:workflowParameters.headers.unitType')}</span>
                </div>
                <div className="AppCore__WorkflowParameterSettings__parameter_rows">
                    {props.parameters.map((parameter) => (
                        <ParameterRow
                            key={parameter.id}
                            parameter={parameter}
                            parameterTypes={props.parameterTypes}
                            onDeleteClick={() => setDeletingParameter(parameter.id)}
                            onEditClick={() => {
                                setEditParameter(parameter)
                                setIsFormOpen(true)
                            }}
                        />
                    ))}
                    {props.parameters.length === 0 && (
                        <div className="AppCore__WorkflowParameterSettings__parameter_example_row">
                            <span>{window.i18n('appcore:workflowParameters.examples.name')}</span>
                            <span>{window.i18n('appcore:workflowParameters.examples.unitType')}</span>
                        </div>
                    )}
                </div>
            </div>
            <AddParameterForm
                isOpen={isFormOpen}
                setIsOpen={setIsFormOpen}
                parameters={props.parameters}
                parameterTypes={props.parameterTypes}
                onConfirm={addNewParameter}
                editParameter={editParameter}
            />
            <ConfirmModal
                type="warning"
                open={deletingParameter !== undefined}
                onCancel={() => setDeletingParameter(undefined)}
                onConfirmClicked={() => deleteParameter()}
                title={window.i18n('appcore:workflowParameters.titles.delete')}
                confirmButtonText={window.i18n('appcore:workflowParameters.buttons.confirmDelete')}
            >
                <span className="AppCore__WorkflowParameterSettings__parameter_delete_description">
                    {window.i18n('appcore:workflowParameters.titles.deleteDescription')}
                </span>
            </ConfirmModal>
        </>
    )
}

function getMenuItems(): MenuProps['items'] {
    return [
        {
            label: window.i18n('appcore:workflowParameters.buttons.edit'),
            icon: <zaptic-icon icon-name="edit" />,
            key: 'edit',
        },
        {
            label: window.i18n('appcore:common.delete'),
            icon: <zaptic-icon icon-name="delete-trash" />,
            key: 'delete',
            danger: true,
        },
    ]
}

function ParameterRow({
    parameter,
    onDeleteClick,
    onEditClick,
    parameterTypes,
}: {
    parameter: InsertableWorkflowParameter
    onDeleteClick: (parameterId: UUID) => void
    onEditClick: () => void
    parameterTypes: Array<{ id: number; name: string }>
}) {
    function contextMenuClickHandler(): MenuProps['onClick'] {
        return (e) => {
            if (e.key === 'delete') {
                onDeleteClick(parameter.id)
            } else if (e.key === 'edit') {
                onEditClick()
            }
        }
    }

    return (
        <div className="AppCore__WorkflowParameterSettings__parameter_row">
            <span>{parameter.name}</span>
            <span>{parameterTypes.find((ref) => ref.id === parameter.referenceId)?.name || ''}</span>
            <Dropdown
                menu={{
                    items: getMenuItems(),
                    onClick: contextMenuClickHandler(),
                }}
                trigger={['click']}
            >
                <IconButton
                    size="small"
                    border={false}
                    data-testid="workflow-parameter-context-menu"
                    iconName="ellipsis"
                />
            </Dropdown>
        </div>
    )
}

function AddParameterForm({
    isOpen,
    setIsOpen,
    parameters,
    parameterTypes,
    onConfirm,
    editParameter,
}: {
    isOpen: boolean
    setIsOpen: (open: boolean) => void
    parameters: InsertableWorkflowParameter[]
    parameterTypes: Array<{ id: number; name: string }>
    onConfirm: (name: string, unitType: number, editingParameterId?: UUID) => void
    editParameter: InsertableWorkflowParameter
}) {
    const [name, setName] = React.useState('')
    const [errors, setErrors] = React.useState<{ [name: string]: boolean }>({
        nameMissing: false,
        nameAlreadyExists: false,
        unitTypeMissing: false,
    })
    const [unitType, setUnitType] = React.useState<number | undefined>(undefined)

    React.useEffect(() => {
        setName(editParameter?.name || '')
        setUnitType(editParameter?.referenceId)
    }, [editParameter])

    function validateName(newName: string) {
        const newErrors = {
            nameMissing: false,
            nameAlreadyExists: false,
        }

        if (!newName) {
            newErrors.nameMissing = true
        }

        if (parameters.some((param) => param.name?.toLowerCase() === newName?.toLowerCase())) {
            newErrors.nameAlreadyExists = true
        }

        return newErrors
    }

    function closeForm() {
        setName('')
        setUnitType(undefined)
        setErrors({ nameMissing: false, nameAlreadyExists: false, unitTypeMissing: false })
        setIsOpen(false)
    }

    function saveClicked() {
        const newErrors: { [name: string]: boolean } = validateName(name)

        if (!unitType) {
            newErrors.unitTypeMissing = true
        }

        setErrors(newErrors)

        if (newErrors.nameMissing || newErrors.nameAlreadyExists || newErrors.unitTypeMissing) {
            return
        }

        onConfirm(name, unitType, editParameter?.id)
        closeForm()
    }

    function onNameChange(e: React.ChangeEvent<HTMLInputElement>) {
        setErrors({ ...errors, ...validateName(e.target.value) })
        setName(e.target.value)
    }

    function onUnitTypeChange(unitType: number) {
        setErrors({ ...errors, unitTypeMissing: false })
        setUnitType(unitType)
    }

    return (
        <ConfirmModal
            className="ConfirmModalStories_confirm-modal"
            dataIdPrefix="workflow-parameter-settings-add-parameter-form"
            title={
                editParameter
                    ? window.i18n('appcore:workflowParameters.titles.edit')
                    : window.i18n('appcore:workflowParameters.titles.add')
            }
            open={isOpen}
            type={'info'}
            closable
            buttonOrientation={'horizontal'}
            buttonSize="default"
            confirmButtonText={
                editParameter
                    ? window.i18n('shared:common.saveChanges')
                    : window.i18n('appcore:workflowParameters.buttons.add')
            }
            onCancel={() => closeForm()}
            onConfirmClicked={saveClicked}
        >
            <div className="AppCore__WorkflowParameterSettings__add-parameter-form">
                <Flex vertical flex={1} gap={'8px'}>
                    <span>{window.i18n('appcore:workflowParameters.headers.name')}</span>
                    <Input
                        autoComplete="off"
                        maxLength={50}
                        data-testid="workflow-parameter-name-input"
                        placeholder={window.i18n('appcore:workflowParameters.placeholders.name')}
                        className="input_field"
                        onChange={onNameChange}
                        value={name}
                    />
                    {errors.nameMissing && (
                        <Flex className="AppCore__WorkflowParameterSettings__error">
                            <zaptic-icon icon-name="red-warning-icon"></zaptic-icon>
                            <span className="has-text-danger">
                                {window.i18n('appcore:workflowParameters.validation.name.empty')}
                            </span>
                        </Flex>
                    )}

                    {errors.nameAlreadyExists && (
                        <Flex className="AppCore__WorkflowParameterSettings__error">
                            <zaptic-icon icon-name="red-warning-icon"></zaptic-icon>
                            <span className="has-text-danger">
                                {window.i18n('appcore:workflowParameters.validation.name.unique')}
                            </span>
                        </Flex>
                    )}
                </Flex>

                <Flex vertical flex={1} gap={'8px'}>
                    <span>{window.i18n('appcore:workflowParameters.headers.unitType')}</span>
                    <Select
                        data-testid="workflow-parameter-unit-type-select"
                        placeholder={window.i18n('appcore:workflowParameters.placeholders.unitType')}
                        showSearch
                        allowClear={true}
                        className="has-fullwidth"
                        suffixIcon={<DropdownArrowIcon expanded={false} />}
                        size="large"
                        popupClassName="AppCore__WorkflowParameterSettings__unit_type_select"
                        options={parameterTypes
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((type) => ({ value: type.id, label: type.name }))}
                        onChange={onUnitTypeChange}
                        value={unitType}
                        dropdownRender={dropdownRender}
                        optionFilterProp="label"
                        disabled={editParameter !== undefined}
                    />
                    {editParameter && (
                        <Flex className="AppCore__WorkflowParameterSettings__disabled">
                            <span>{window.i18n('appcore:workflowParameters.validation.unitType.disabled')}</span>
                        </Flex>
                    )}
                    {errors.unitTypeMissing && (
                        <Flex className="AppCore__WorkflowParameterSettings__error">
                            <zaptic-icon icon-name="red-warning-icon"></zaptic-icon>
                            <span className="has-text-danger">
                                {window.i18n('appcore:workflowParameters.validation.unitType.empty')}
                            </span>
                        </Flex>
                    )}
                </Flex>
            </div>
        </ConfirmModal>
    )
}

function dropdownRender(menu: React.ReactElement<any, string | React.JSXElementConstructor<any>>) {
    return <PreventClickPropagation>{menu}</PreventClickPropagation>
}
